import { getFileTitle } from "files/hooks/useFileTitle";
import { resolveRouterPath, useFilesSearch } from "files/hooks/useFiles";
import { ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight } from "lucide-react";
import pluralize from "pluralize";
import {
  BooleanParam,
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from "use-query-params";
import { LlamaIcon } from "~/assets/icons";
import { Button } from "~/components/ui/button";
import { FileRow } from "~/components/ui/file-row";
import { Label } from "~/components/ui/label";
import { ListCard } from "~/components/ui/list-card";
import useProjectsAccessList from "~/hooks/useProjectsAccessList";
import { cn } from "~/lib/utils";
import useIsWorkbenchFileContext from "~/pages/files/hooks/useIsWorkbenchFileContext";

const PAGE_SIZE = 15;

// Matches `components/ui/directory.tsx`'s header exactly, so switching
// between browsing and search results doesn't visibly change the listing's
// chrome.
const DirectoryHeader = () => (
  <Label className="flex rounded-t-lg bg-background gap-8 px-5 py-3 font-medium border-b border-gray-300 text-[13px] text-gray-900/50">
    <span className="grow shrink basis-0 text-nowrap">Name</span>
    <span className="w-[100px] text-end">Size</span>
    <span className="w-[236px]">Updated</span>
  </Label>
);

const buildPaginationString = (pageNum: number, pageSize: number, total: number): string => {
  const start = (pageNum - 1) * pageSize + 1;
  const end = pageNum * pageSize < total ? pageNum * pageSize : total;

  if (total > 0 && total <= pageSize) {
    return `${total.toLocaleString()} ${pluralize("result", total)}`;
  }

  return `${start} - ${end} of ${total.toLocaleString()} ${pluralize("result", total)}`;
};

const SearchResultsPagination = ({ total, page }: { total: number; page: number }) => {
  const [, setParams] = useQueryParams({ page: NumberParam });
  const pageCount = Math.ceil(total / PAGE_SIZE) || 0;
  const prevDisabled = page === 1;
  const nextDisabled = page === pageCount;

  if (total <= PAGE_SIZE) {
    return null;
  }

  const goTo = (newPage: number) => {
    const scrollWrapper = document.querySelector("main");
    setParams({ page: newPage === 1 ? undefined : newPage }, "replaceIn");
    setTimeout(() => scrollWrapper?.scrollTo({ top: 0, behavior: "smooth" }), 0);
  };

  return (
    <div className="flex h-[43px] items-center">
      <div className="flex-1 truncate text-[13px] text-muted-foreground">
        {buildPaginationString(page, PAGE_SIZE, total)}
      </div>
      <div className="flex items-center gap-0.5">
        <Button
          variant="ghost"
          size="icon"
          className="size-7"
          disabled={prevDisabled}
          onClick={() => goTo(1)}
        >
          <ChevronsLeft className="size-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="size-7"
          disabled={prevDisabled}
          onClick={() => goTo(page - 1)}
        >
          <ChevronLeft className="size-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="size-7"
          disabled={nextDisabled}
          onClick={() => goTo(page + 1)}
        >
          <ChevronRight className="size-4" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="size-7"
          disabled={nextDisabled}
          onClick={() => goTo(pageCount)}
        >
          <ChevronsRight className="size-4" />
        </Button>
      </div>
    </div>
  );
};

/**
 * Shadcn rebuild of `files/components/SearchResults`, for the
 * nextgen-embedded Files tab only — reuses the same shadcn `FileRow` the
 * normal directory listing uses, so results get correct dark-mode styling
 * without a second MUI listing implementation.
 */
export const EmbeddedSearchResults = () => {
  const [params, setParams] = useQueryParams({
    q: withDefault(StringParam, ""),
    page: withDefault(NumberParam, 1),
    root: BooleanParam,
  });
  const isGlobalFileContext = !useIsWorkbenchFileContext();
  const projectAccess = useProjectsAccessList();
  const { data: searchResponse, isLoading, error } = useFilesSearch();
  const response = searchResponse?.items;
  const truncated = searchResponse?.metadata.truncated;
  const showNoResults = !isLoading && response?.length === 0;

  const totalNumberOfPages = Math.ceil((response?.length ?? 0) / PAGE_SIZE);
  const isLastPage = params.page === totalNumberOfPages;

  // Paginate client side
  const start = params.page ? (params.page - 1) * PAGE_SIZE : 0;
  const paginatedFiles = response?.slice(start, start + PAGE_SIZE) ?? [];

  const clearSearch = () => setParams({ q: undefined, page: undefined, root: undefined });

  if (showNoResults || error?.status === 504) {
    return (
      <div className="flex min-h-[300px] flex-col items-center justify-center gap-3 rounded-lg border border-border bg-background p-6 text-center">
        <LlamaIcon
          opacity={0.4}
          width={50}
          className="text-primary"
          transform="matrix(-1, 0, 0, 1, 0, 0), scale(1.2)"
        />
        <p className="text-sm">
          {showNoResults ? "No results found" : "Search took too long to complete"}
        </p>
        <Button onClick={clearSearch}>Back to Files</Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <ListCard
        header={<DirectoryHeader />}
        loading={isLoading}
        placeholder={{ className: "h-[34px]" }}
        className={cn("border border-border")}
      >
        {paginatedFiles.map((file) => {
          const pathSegments = resolveRouterPath(file.key, isGlobalFileContext).split("/");
          const { title: rootTitle } = getFileTitle(
            pathSegments[0],
            isGlobalFileContext,
            projectAccess
          );
          const subtitle = [rootTitle, ...pathSegments.slice(1)].join("/");
          return (
            <FileRow
              key={file.key}
              fileKey={file.key}
              navigation="url-based"
              fileMetadata={{
                subtitle,
                type: file.type,
                size: file.size,
                pendingAction: file.pending_action,
                updatedAt: file.updated_at,
                s3Uri: file.s3_uri,
              }}
            />
          );
        })}
      </ListCard>
      {!isLoading && (
        <SearchResultsPagination total={response?.length ?? 0} page={params.page ?? 1} />
      )}
      {truncated && isLastPage && (
        <p className="text-center text-sm text-muted-foreground">
          Search result limit exceeded. Try refining your search
        </p>
      )}
    </div>
  );
};
